/**
 * Incident investigation list (Module 24) — `GET /investigations`.
 *
 * Migrated onto the CoOrdio list-page pattern (see `ProjectListPage.tsx`, the reference): the grid
 * owns paging, search, sorting and filtering, so the page keeps only the column plan, the row
 * actions, one `DeleteDialog` (used here for the non-destructive Complete confirmation) and the
 * single piece of refetch state (`listVersion`).
 *
 * Sibling route note: `InvestigationTemplatesListPage` lives at `ROUTES.INVESTIGATION_TEMPLATES`
 * and is a separate page with its own heading and its own create target. This one is the sessions
 * list — heading "Incident Investigations", create target `ROUTES.INVESTIGATION_NEW`.
 *
 * ── Column plan ──────────────────────────────────────────────────────────────────────────────────
 *  field                    | header     | width | cell type        | filter
 *  -------------------------|------------|-------|------------------|--------------------------------
 *  title                    | Title      |  26%  | text (clickable) | toolbar search (searchTerm)
 *  severity                 | Severity   |  12%  | custom → Chip    | popover select (severity)
 *  investigationStatus      | Status     |  14%  | custom → Chip    | status tabs + popover select
 *  incidentNumber           | Incident # |  14%  | text             | —
 *  agentName                | Agent      |  14%  | text             | —
 *  investigationStartedAt   | Started    |  20%  | date (locale)    | popover date (startDate/endDate)
 *
 * Dropped (present on the old MUI table): `description` (was a muted second line under the title),
 * `investigationCompletedAt` (Started is the date a queue is read on; the completion time is on the
 * detail page), the combined `Steps / Msg / Corr` counts column (three numbers crammed into one
 * cell that answered no question a list is asked), `createdAt`, `lastActivityAt`, and the
 * root-cause / remediation / prevention prose.
 *
 * Service filters NOT offered: `relatedIncidentId` is accepted by `getInvestigations` but is a raw
 * GUID, so it is left to deep links. The endpoint accepts no sort parameters at all, which is why
 * no column is marked `sortable`.
 */

import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Button,
  CordioAbpPagedDataTable,
  Chip,
  ShellHeader,
  type ChipColor,
  type CordioDataTableAction,
  type CordioDataTableColumn,
  type CordioDataTableListToolbarConfig,
} from '@coordio/ui';
import { CheckCircle2, Plus } from 'lucide-react';

import { createCortexPagedDataSource, type CortexPagedQuery } from '../components/data-display/cortexPagedDataSource';
import { DeleteDialog, useDeleteDialog } from '../components/delete-dialog';
import { investigationService } from '../services/investigationService';
import { useNotificationStore } from '../store/notificationStore';
import { ROUTES } from '../utils/routes';

import {
  IncidentSeverities,
  InvestigationStatuses,
  type IncidentInvestigationSessionDto,
  type IncidentSeverity,
  type InvestigationFilters,
  type InvestigationStatus,
} from '../types/investigation';

import './InvestigationListPage.scss';

/**
 * Quick-filter tabs.
 *
 * `value` is load-bearing twice: the grid writes it to `paramKey`, and it builds each tab's test id
 * as `cordio-datatable-status-tab-<value>`. The All tab therefore declares the literal `'all'` —
 * an empty value would emit `cordio-datatable-status-tab-` and GRID-13 would select nothing.
 *
 * The other two carry the API's own numeric values as strings (the backend enum is numeric), so the
 * only translation {@link toInvestigationQuery} performs is `'all'` → no filter, and `String` →
 * `Number`. `paramKey` is the synthetic {@link STATUS_TAB_PARAM_KEY} because `'all'` is a UI
 * concept the endpoint would reject.
 */
const STATUS_TAB_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'In Progress', value: String(InvestigationStatuses.InProgress) },
  { label: 'Completed', value: String(InvestigationStatuses.Completed) },
] as const;

/** The synthetic toolbar key the status tabs write to. Never sent to the API as-is. */
const STATUS_TAB_PARAM_KEY = 'statusTab';

/**
 * Every status, offered in the filter popover under the SAME key as the tabs.
 *
 * WHY one key rather than a second `investigationStatus` filter: the old page had a status dropdown
 * covering all five statuses and the tabs above reach only two. Pointing both surfaces at
 * `statusTab` keeps that reach without inventing a precedence rule — picking Paused in the popover
 * simply leaves no tab highlighted, and picking a tab updates the popover.
 */
const STATUS_FILTER_OPTIONS = [
  { label: 'Not Started', value: String(InvestigationStatuses.NotStarted) },
  { label: 'In Progress', value: String(InvestigationStatuses.InProgress) },
  { label: 'Paused', value: String(InvestigationStatuses.Paused) },
  { label: 'Completed', value: String(InvestigationStatuses.Completed) },
  { label: 'Cancelled', value: String(InvestigationStatuses.Cancelled) },
];

const SEVERITY_FILTER_OPTIONS = [
  { label: 'Low', value: String(IncidentSeverities.Low) },
  { label: 'Medium', value: String(IncidentSeverities.Medium) },
  { label: 'High', value: String(IncidentSeverities.High) },
  { label: 'Critical', value: String(IncidentSeverities.Critical) },
];

const SEVERITY_LABELS: Record<IncidentSeverity, string> = {
  [IncidentSeverities.Low]: 'Low',
  [IncidentSeverities.Medium]: 'Medium',
  [IncidentSeverities.High]: 'High',
  [IncidentSeverities.Critical]: 'Critical',
};

const STATUS_LABELS: Record<InvestigationStatus, string> = {
  [InvestigationStatuses.NotStarted]: 'Not Started',
  [InvestigationStatuses.InProgress]: 'In Progress',
  [InvestigationStatuses.Paused]: 'Paused',
  [InvestigationStatuses.Completed]: 'Completed',
  [InvestigationStatuses.Cancelled]: 'Cancelled',
};

const severityChipColor = (severity: IncidentSeverity): ChipColor => {
  switch (severity) {
    case IncidentSeverities.Low:
      return 'green';
    case IncidentSeverities.Medium:
      return 'amber';
    case IncidentSeverities.High:
    case IncidentSeverities.Critical:
      return 'red';
    default:
      return 'default';
  }
};

const statusChipColor = (status: InvestigationStatus): ChipColor => {
  switch (status) {
    case InvestigationStatuses.Completed:
      return 'green';
    case InvestigationStatuses.InProgress:
      return 'blue';
    case InvestigationStatuses.Paused:
      return 'amber';
    case InvestigationStatuses.Cancelled:
      return 'red';
    default:
      return 'default';
  }
};

/** `undefined` unless the value is a non-empty string that parses to a finite number. */
function asNumber(value: unknown): number | undefined {
  if (typeof value !== 'string' || value.trim() === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() !== '' ? value : undefined;
}

/**
 * Turns the grid's query into the one `investigationService.getInvestigations` accepts.
 *
 * Built key-by-key rather than by spreading `query`: `InvestigationFilters` is a closed set, the
 * numeric enums arrive from the toolbar as strings, and the synthetic status-tab key must not leak.
 * A filter the endpoint does not accept is never sent.
 */
function toInvestigationQuery(query: CortexPagedQuery): InvestigationFilters {
  const statusTab = query[STATUS_TAB_PARAM_KEY];
  const status = statusTab === 'all' ? undefined : asNumber(statusTab);

  return {
    pageNumber: query.pageNumber,
    pageSize: query.pageSize,
    searchTerm: asString(query.searchTerm),
    investigationStatus: status as InvestigationStatus | undefined,
    severity: asNumber(query.severity) as IncidentSeverity | undefined,
    startDate: asString(query.startDate),
    endDate: asString(query.endDate),
  };
}

const investigationDetailPath = (id: string) => ROUTES.INVESTIGATION_DETAIL.replace(':id', id);
const investigationEditPath = (id: string) => ROUTES.INVESTIGATION_EDIT.replace(':id', id);

export const InvestigationListPage = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotificationStore();

  /** Bumped after a mutation to rebuild `dataSource` and make the grid refetch. */
  const [listVersion, setListVersion] = useState(0);

  const { dialogRef, pendingItem, openDelete, closeDelete } =
    useDeleteDialog<IncidentInvestigationSessionDto>();

  const dataSource = useMemo(
    () =>
      createCortexPagedDataSource<IncidentInvestigationSessionDto>((query) =>
        investigationService.getInvestigations(toInvestigationQuery(query)),
      ),
    // `listVersion` is a deliberate dependency: changing the identity of `dataSource` is the
    // documented way to make the grid reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [listVersion],
  );

  const columns = useMemo<ReadonlyArray<CordioDataTableColumn<IncidentInvestigationSessionDto>>>(
    () => [
      { field: 'title', header: 'Title', type: 'text', width: '26%', clickable: true },
      {
        field: 'severity',
        header: 'Severity',
        type: 'custom',
        width: '12%',
        customCell: (row) => (
          <Chip label={SEVERITY_LABELS[row.severity] ?? 'Unknown'} color={severityChipColor(row.severity)} />
        ),
      },
      {
        field: 'investigationStatus',
        header: 'Status',
        type: 'custom',
        width: '14%',
        // A Chip rather than the built-in `status` cell: the DTO carries a numeric enum, so the
        // label has to be looked up, and this keeps the colours matching the tabs above the grid.
        customCell: (row) => (
          <Chip
            label={STATUS_LABELS[row.investigationStatus] ?? 'Unknown'}
            color={statusChipColor(row.investigationStatus)}
          />
        ),
      },
      { field: 'incidentNumber', header: 'Incident #', type: 'text', width: '14%' },
      { field: 'agentName', header: 'Agent', type: 'text', width: '14%' },
      {
        field: 'investigationStartedAt',
        header: 'Started',
        type: 'date',
        width: '20%',
        dateFormat: 'locale',
      },
    ],
    [],
  );

  const listToolbar = useMemo<CordioDataTableListToolbarConfig>(
    () => ({
      search: { placeholder: 'Search title, description or incident #' },
      statusTabs: {
        paramKey: STATUS_TAB_PARAM_KEY,
        ariaLabel: 'Investigation status filter',
        options: [...STATUS_TAB_OPTIONS],
      },
      filters: [
        // Same key as the tabs on purpose — see STATUS_FILTER_OPTIONS.
        {
          key: STATUS_TAB_PARAM_KEY,
          label: 'Status',
          type: 'select',
          options: STATUS_FILTER_OPTIONS,
          paramLabel: 'investigationStatus',
        },
        { key: 'severity', label: 'Severity', type: 'select', options: SEVERITY_FILTER_OPTIONS },
        { key: 'startDate', label: 'Started From', type: 'date' },
        { key: 'endDate', label: 'Started To', type: 'date' },
      ],
      filterButtonLabel: 'Filter',
    }),
    [],
  );

  const actions = useMemo<ReadonlyArray<CordioDataTableAction<IncidentInvestigationSessionDto>>>(
    () => [
      {
        icon: 'eye',
        tooltip: 'View investigation',
        onClick: (row) => navigate(investigationDetailPath(row.id)),
      },
      {
        icon: 'square-pen',
        tooltip: 'Edit investigation',
        onClick: (row) => navigate(investigationEditPath(row.id)),
      },
      {
        icon: 'check',
        tooltip: 'Complete investigation',
        // `visible` rather than a disabled control: completing an already-completed investigation
        // has nothing to say.
        visible: (row) => row.investigationStatus !== InvestigationStatuses.Completed,
        onClick: (row) => openDelete(row),
      },
    ],
    [navigate, openDelete],
  );

  const handleCompleteConfirm = useCallback(async () => {
    if (!pendingItem) return;

    await investigationService.completeInvestigation(pendingItem.id);
    showNotification('Investigation completed successfully', 'success');

    setListVersion((v) => v + 1);
  }, [pendingItem, showNotification]);

  return (
    <div className="investigation-list" data-testid="investigation-list-page">
      <ShellHeader
        title="Incident Investigations"
        description="Agent-assisted incident investigations, with their severity, progress and root-cause work."
        data-testid="investigation-list-header"
        actions={
          <Button
            type="button"
            variant="default"
            size="sm"
            className="gap-2"
            data-testid="add-investigation-btn"
            onClick={() => navigate(ROUTES.INVESTIGATION_NEW)}
          >
            <Plus className="h-4 w-4 shrink-0" aria-hidden />
            Create Investigation
          </Button>
        }
      />

      <div data-testid="investigation-list-grid">
        <CordioAbpPagedDataTable<IncidentInvestigationSessionDto>
          className="investigation-list__table"
          columns={columns}
          dataSource={dataSource}
          initialPageSize={25}
          maxBodyHeight="520px"
          listToolbar={listToolbar}
          actions={actions}
          onRowClick={(row) => navigate(investigationDetailPath(row.id))}
          onCellClick={({ row }) => navigate(investigationDetailPath(row.id))}
        />
      </div>

      <DeleteDialog
        ref={dialogRef}
        title="Complete investigation?"
        entityLabel={pendingItem?.title}
        dialogIcon={<CheckCircle2 className="delete-dialog__icon" />}
        description="The investigation is marked complete and stops accepting new steps and messages."
        confirmLabel="Complete"
        // Not destructive: completing an investigation preserves everything it recorded.
        confirmVariant="default"
        busyLabel="Completing…"
        onConfirm={handleCompleteConfirm}
        onCancel={closeDelete}
      />
    </div>
  );
};
